import { useCallback, useEffect, memo, useRef, useMemo, useLayoutEffect } from "react";
import { VariableSizeList, areEqual } from "react-window";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Button, FormControl, Pagination, Icon, IconButton } from "@/comn/components";
import { comnUtils } from "@/comn/utils";
import { validateValue } from "./utils";
import { GridContextProvider, useGridContext } from "./reducer";
import { GridTable } from "./GridTable";
import { GridCompare } from "./GridCompare";
import { useToast } from "@/comn/hooks";

type TGridCell = Record<string, any>;
type TGridRow = Record<string, any>;
type TGridRowContext = { backgroundColor: "yellow" | "red" | "blue" };
type TGridCellContext = { textColor: "yellow" | "red" | "blue"; backgroundColor: "yellow" | "red" | "blue" };

export type TGridRenderSchema = {
    [key: string]: {
        head?: {
            [name: string]: (props: { id: string; binding: string; head: string }) => any;
        };
        cell?: {
            [name: string]: (props: {
                rowValues: any;
                value: any;
                binding: string;
                formattedValue: any;
                control: any;
            }) => any;
        };
        edit?: {
            [name: string]: (props: {
                rowValues: any;
                value: any;
                binding: string;
                formattedValue: any;
                control: any;
            }) => any;
        };
        body?: {
            [name: string]: (props: {
                rowValues: any;
                value: any;
                binding: string;
                formattedValue: any;
                control: any;
            }) => any;
        };
        checkbox?: (data: TGridRow) => boolean;
        radio?: (data: TGridRow) => boolean;
        row?: (data: TGridRow, context: TGridRowContext) => boolean;
    };
};

export type TGridRender = {
    row?: (data: TGridRow, context: TGridRowContext) => boolean;
    checkbox?: (data: TGridRow) => boolean;
    radio?: (data: TGridRow) => boolean;
    cell?: any;
    edit?: any;
    head?: any;
    body?: any;
};

type GridProps = {
    _grid?: any;
    data?: any;
    render?: any;
    onCellClick?: any;
    onRowClick?: any;
    onRowCheck?: any;
    onRowSelect?: any;
    onPageChange?: any;
    onSizeChange?: any;
    onBeforeUpdate?: any;
    checkAfterSet?: any;
};

export const Grid = (props: GridProps) => {
    return (
        <GridContextProvider {...props}>
            <Component />
        </GridContextProvider>
    );
};

const GridHead = (props: any) => {
    const { t } = useTranslation();

    const {
        _grid,
        test,
        headSchema,
        gridTemplateColumns,
        sort,
        hasCheckbox,
        hasRadio,
        hasIndex,
        hasGroup,
        hasSort,
        checkbox,
        checked,
    } = useGridContext();

    const checkedAll = (
        _grid.current._render?.checkbox
            ? test.filter((_: any) => {
                  return _grid.current._render.checkbox(_);
              })
            : test
    ).every(({ __key }: any) => {
        return checked.some((_: any) => _ === __key);
    });

    const handleCheck = (event: any) => {
        _grid.current._handleCheckAll(event);
    };

    const $ = useRef(null);

    const refCallback = useCallback((element: any) => {
        if (element) $.current = element;
    }, []);

    useLayoutEffect(() => {
        let observer: any;
        if ($.current) {
            observer = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const currHeight = entry.contentRect.height;
                    if (currHeight === 0) return;
                    const prevHeight = _grid.current._headHeight;
                    if (currHeight !== prevHeight) {
                        _grid.current._setHeadHeight(currHeight);
                    }
                }
            });
            observer.observe($.current);
        }
        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <div ref={refCallback} className="flex w-full min-w-fit border-l bg-border sticky top-0 z-[1]">
            {hasGroup && <OptionCell />}
            {hasCheckbox && (
                <OptionCell>
                    <input
                        type="checkbox"
                        disabled={checkbox === "readOnly" || undefined}
                        checked={checkedAll}
                        onChange={handleCheck}
                    />
                </OptionCell>
            )}
            {hasRadio && <OptionCell />}
            {hasIndex && <OptionCell />}

            <div className="grid w-full" style={{ gridTemplateColumns }}>
                {headSchema.map((row: any, rowIndex: any) => {
                    return row.map((cel: any, colIndex: any) => {
                        if (!cel) return null;
                        if (cel.show === false) return null;

                        const { id, header, binding, required, rowspan, colspan } = cel;

                        const cellContent =
                            _grid.current._render?.head?.[binding]?.({
                                id,
                                binding,
                                header: t(header),
                            }) ||
                            t(header) ||
                            binding;

                        const SortButton = () => {
                            const sortIcon =
                                sort[binding]?.val === "asc"
                                    ? "barsUp"
                                    : sort[binding]?.val === "desc"
                                      ? "barsDown"
                                      : "bars";

                            const handleClick = () => {
                                _grid.current._handleSort(binding);
                            };

                            return (
                                <button className="relative ml-0.5" onClick={handleClick}>
                                    <Icon icon={sortIcon} size="xs" />
                                    <span className="absolute text-[10px] bottom-0 right-0 translate-x-full">
                                        {sort[binding]?.seq}
                                    </span>
                                </button>
                            );
                        };

                        return (
                            <div
                                key={_grid.current._key + ".gh." + rowIndex + "." + colIndex}
                                className={classNames(
                                    "p-1 bg-header border-r border-b min-h-[2.5rem] flex items-center justify-center text-center font-semibold [word-break:break-word]",
                                    required && "after:content-['*'] after:text-error",
                                )}
                                style={{
                                    gridRow: `${rowIndex + 1} / span ${rowspan ?? 1}`,
                                    gridColumn: `${colIndex + 1} / span ${colspan ?? 1}`,
                                }}
                            >
                                {cellContent}
                                {hasSort && <SortButton />}
                            </div>
                        );
                    });
                })}
            </div>
        </div>
    );
};

const GridBody = () => {
    const { _grid, headSchema, gridTemplateColumns, sort, test, height, headHeight } = useGridContext();

    const listRefCallback = (ref: any) => {
        if (!ref) return;
        if (_grid.current._listRef) return;
        _grid.current._listRef = ref;
    };

    const itemSize = (index: any) => {
        return _grid.current._rect[index]?.["height"] || _grid.current._rect[0]?.["height"] || 40;
    };

    const innerElementType = useCallback(
        memo((props: any) => {
            const { children, style } = props;

            useEffect(() => {
                if (_grid.current._autoHeight) {
                    if (_grid.current._height !== style.height) {
                        _grid.current._height = style.height;
                        _grid.current._readjustHeight?.();
                    }
                }
            });

            return (
                <>
                    <GridHead _grid={_grid} />
                    <div className="relative">{children}</div>
                </>
            );
        }),
        [headSchema, gridTemplateColumns, sort],
    );

    return (
        <VariableSizeList
            ref={listRefCallback}
            className="bg-border break-all border-t"
            style={{ willChange: "" }}
            innerElementType={innerElementType}
            itemSize={itemSize}
            itemCount={test.length}
            height={height + headHeight}
            width="100%"
        >
            {Row}
        </VariableSizeList>
    );
};

/**
 * # Grid
 */
const Component = () => {
    const { _grid, bodySchema, page, size, totalItemCount, hasAdd, hasDelete, hasPagination } = useGridContext();

    return (
        <div className="flex flex-col w-full">
            {(hasAdd || hasDelete) && (
                <div className="flex justify-end gap-1 mb-2">
                    {hasAdd && <Button onClick={() => _grid.current._handleAdd()}>Add</Button>}
                    {hasDelete && <Button onClick={() => _grid.current._handleDelete("all")}>Delete</Button>}
                </div>
            )}

            {/* Body */}
            {bodySchema && (
                <div className="relative mb-2 w-full">
                    <GridBody />
                    <div className="absolute bottom-0 left-0 w-full border-b bg-border" />
                </div>
            )}

            {/* Pagination */}
            {hasPagination && (
                <Pagination
                    page={page}
                    size={size}
                    onChangePage={(next) => {
                        _grid.current._handlePage(next);
                        if (_grid.current._onPageChange) _grid.current._onPageChange(next);
                    }}
                    onChangeSize={(next) => {
                        _grid.current._handleSize(next);
                        if (_grid.current.onSizeChange) _grid.current.onSizeChange(next);
                    }}
                    totalCount={totalItemCount}
                />
            )}

            <GridTable />
            <GridCompare />
        </div>
    );
};

/** row */
const Row = memo((props: any) => {
    const { index, style } = props;

    const {
        _grid,
        test,
        checked,
        selectedRow,
        selectedCel,
        totalCount,
        editingRow,
        pagination,
        index: indexType,
        page,
        size,
        refreshCount,
        bodySchema,
        groupSchema,
        groupFootSchema,
        footSchema,
        gridTemplateColumns,
        checkbox,
        hasCheckbox,
        hasIndex,
        hasRadio,
        hasGroup,
    } = useGridContext();

    const { showToast } = useToast();

    const row = test[index];
    const rowKey = row?.__key;
    const rowType = row?.__type;
    const rowIndex = row?.__index;
    const rowContext: TGridRowContext = row?.__context || {};
    const { backgroundColor } = rowContext;

    const isSelected = selectedRow === rowKey;
    const isChecked = checked.some((_: any) => _ === rowKey);

    const { t } = useTranslation();
    const resizeObserverRef = useRef<any>(null);
    const rowRefCallback = useCallback((ref: any) => {
        if (!ref) return;

        resizeObserverRef.current = new ResizeObserver((entries) => {
            requestAnimationFrame(() => {
                entries.forEach((value) => {
                    if (value.contentRect.height === 0) return;
                    if (_grid.current._rect[index]?.height !== value.contentRect.height) {
                        _grid.current._rect[index] = value.contentRect;
                        _grid.current._listRef?.resetAfterIndex(index);
                        if (_grid.current._autoHeight) {
                            _grid.current._readjustHeight();
                        }
                    }
                });
            });
        });

        resizeObserverRef.current.observe(ref);
    }, []);

    useEffect(() => {
        return () => {
            resizeObserverRef.current.disconnect();
        };
    }, []);

    const _mem1 = JSON.stringify([
        gridTemplateColumns,
        bodySchema,
        row,
        editingRow,
        rowContext,
        isSelected,
        selectedCel,
        refreshCount,
    ]);

    const Body = useMemo(() => {
        if (rowType !== "group" && rowType !== "groupFoot" && rowType !== "empty" && rowType !== "foot")
            return (
                <div className={classNames("grid w-full group")} style={{ gridTemplateColumns }}>
                    {bodySchema.map((_: any, rowIndex: any) => {
                        return _.map((cel: any, colIndex: any) => {
                            if (!cel) return null;
                            if (cel.show === false) return null;
                            const {
                                binding,
                                align,
                                rowspan,
                                colspan,
                                edit,
                                mode,
                                header,
                                copy = true,
                                truncate,
                                ...FORM
                            } = cel;

                            const celKey = rowKey + ".gb." + rowIndex + "." + colIndex;

                            const BINDING_VAL = row[binding];
                            const FORMATTED_VAL = comnUtils.getFormattedValue(BINDING_VAL, FORM);
                            const VALI = validateValue(BINDING_VAL, _grid.current._rule[binding], cel?.type, row);

                            const CELL_CONTEXT = {
                                binding,
                                rowValues: row,
                                value: BINDING_VAL,
                                formattedValue: FORMATTED_VAL,
                            };

                            // "HEADER" | "EDIT" | "CELL" | "BODY"
                            let CELL_STATUS;
                            if (Boolean(header)) CELL_STATUS = "HEADER";
                            else {
                                const rowCell = editingRow.find(
                                    ({ key, cell }: any) => key === rowKey && cell === binding,
                                )?.edit;
                                const row = editingRow.find(
                                    ({ key, cell }: any) => key === rowKey && cell === undefined,
                                )?.edit;
                                const isBody = !!_grid.current._render?.body?.[binding];

                                CELL_STATUS = isBody
                                    ? "BODY"
                                    : mode === "edit"
                                      ? "EDIT"
                                      : mode === "view"
                                        ? "CELL"
                                        : rowCell ?? row ?? edit
                                          ? "EDIT"
                                          : "CELL";
                            }

                            /*
                FORM START */
                            if (FORM.rightButton?.onClick) {
                                FORM.rightButton.onClick = () => FORM.rightButton.onClick(CELL_CONTEXT);
                            }
                            if (FORM.leftButton?.onClick) {
                                FORM.leftButton.onClick = () => FORM.leftButton.onClick(CELL_CONTEXT);
                            }

                            switch (FORM.type) {
                                case "daterange":
                                case "timerange":
                                    {
                                        const START_VAL = row[FORM.start.binding];
                                        const END_VAL = row[FORM.end.binding];
                                        FORM.start.type = FORM.type === "daterange" ? "date" : "time";
                                        FORM.end.type = FORM.type === "daterange" ? "date" : "time";
                                        FORM.start.value = comnUtils.getFormattedValue(START_VAL, FORM.start);
                                        FORM.end.value = comnUtils.getFormattedValue(END_VAL, FORM.end);

                                        FORM.start.onChange = (event: any) => {
                                            _grid.current._handleUpdate({
                                                ...row,
                                                [FORM.start.binding]: comnUtils.getUnformattedValue(event, FORM.start),
                                            });
                                        };
                                        FORM.end.onChange = (event: any) => {
                                            _grid.current._handleUpdate({
                                                ...row,
                                                [FORM.end.binding]: comnUtils.getUnformattedValue(event, FORM.end),
                                            });
                                        };
                                    }

                                    break;
                                default:
                                    {
                                        if (FORM.type === "number" && align === undefined) cel.align = "right";
                                        FORM.value = FORMATTED_VAL;
                                        FORM.onChange = (event: any) => {
                                            const nextValue = {
                                                ...row,
                                                [binding]: comnUtils.getUnformattedValue(event, FORM),
                                            };
                                            _grid.current._handleUpdate(nextValue);
                                        };
                                    }
                                    break;
                            }
                            FORM.edit = CELL_STATUS === "EDIT";
                            if (truncate) {
                                FORM.truncate = truncate;
                            }
                            FORM.align = align;
                            const Control = <FormControl {...FORM} />;

                            /* FORM END
                             */

                            const BG_COLORS = {
                                blue: "bg-[#bacee0]",
                                yellow: "bg-[#ffeb33]",
                                red: "bg-[#ed3e49]",
                            };

                            const TEXT_COLORS = {
                                blue: "text-[#bacee0]",
                                yellow: "text-[#ffeb33]",
                                red: "text-[#ed3e49]",
                            };

                            let editContext: any = {};
                            let cellContext: any = {};
                            const CustomEdit = _grid.current._render?.edit?.[binding]?.(
                                { ...CELL_CONTEXT, control: Control, schema: cel },
                                editContext,
                            );
                            const CustomCell = _grid.current._render?.cell?.[binding]?.(
                                { ...CELL_CONTEXT, control: Control, schema: cel },
                                cellContext,
                            );

                            const CustomBody = _grid.current._render?.body?.[binding]?.(
                                { ...CELL_CONTEXT, control: Control, schema: cel },
                                cellContext,
                            );

                            const context = (CELL_STATUS === "EDIT" ? editContext : cellContext) as TGridCellContext;
                            const textColor = context.textColor;
                            const bgColor = context.backgroundColor || backgroundColor;

                            const handleClickCell = () => {
                                _grid.current._handleClickCel({
                                    ...CELL_CONTEXT,
                                    key: celKey,
                                });
                            };

                            return (
                                <div
                                    key={celKey}
                                    className="w-full min-h-[2.5rem] border-r border-b"
                                    style={{
                                        gridRow: `${rowIndex + 1} / span ${rowspan ?? 1}`,
                                        gridColumn: `${colIndex + 1} / span ${colspan ?? 1}`,
                                    }}
                                >
                                    <div
                                        className={classNames(
                                            "group/cell relative min-h-[2.4rem] p-1 h-full flex items-center border border-card aria-[invalid=true]:border-error aria-[selected=true]:border-info group-hover:bg-white/50 dark:group-hover:bg-gray/50",
                                            (align === "start" || align === "left") && "justify-start text-left",
                                            (align === "end" || align === "right") && "justify-end text-right",
                                            (align === "center" || align === undefined) && "justify-center text-center",
                                            CELL_STATUS === "HEADER" && "border-header font-semibold",
                                            bgColor
                                                ? BG_COLORS[bgColor]
                                                : CELL_STATUS === "HEADER"
                                                  ? "bg-header"
                                                  : "bg-card",
                                            textColor && TEXT_COLORS[textColor],
                                            isSelected && "bg-lightgray/30",
                                            CELL_STATUS === "CELL" && "[&_.control-text]:min-w-fit",
                                        )}
                                        {...(edit && VALI.isError && { "aria-invalid": true })}
                                        {...(selectedCel?.__key === celKey && { "aria-selected": true })}
                                        onClick={handleClickCell}
                                    >
                                        {CELL_STATUS === "HEADER" && t(header)}
                                        {CELL_STATUS === "EDIT" && (CustomEdit || Control)}
                                        {CELL_STATUS === "CELL" && (CustomCell || Control)}
                                        {CELL_STATUS === "BODY" && (CustomBody || Control)}

                                        {(window.location.hostname === "localhost" ||
                                            window.location.protocol === "https:") &&
                                            copy && (
                                                <button
                                                    className="hidden bg-lightgray/50 rounded-full p-1 border absolute top-[1px] left-[1px] z-[2] transition-colors hover:bg-white/50 dark:text-black dark:bg-lightgray dark:hover:bg-lightgray/50 group-hover/cell:block group-active:[&:not(:hover)]:hidden"
                                                    onClick={(e: any) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        let value = BINDING_VAL || "";

                                                        let reg = /[`~!@#$%^&*()_|+\-=?;:'",.<>\{\}\[\]\\\/]/gim;

                                                        if (BINDING_VAL === undefined) {
                                                            value = e.target.closest("div").outerText || "";
                                                        }
                                                        try {
                                                            window.navigator.clipboard.writeText(
                                                                value.replace(reg, ""),
                                                            );
                                                            showToast({ content: "L_CPY", type: "info" });
                                                        } catch (error) {
                                                            showToast({ content: "msg.osc.00125", type: "error" });
                                                        }
                                                    }}
                                                >
                                                    <Icon icon="clipboard" size="2xs" />
                                                </button>
                                            )}
                                    </div>
                                </div>
                            );
                        });
                    })}
                </div>
            );

        return null;
    }, [_mem1]);

    return (
        <div style={{ ...style }}>
            {/* Empty */}
            {rowType === "empty" && (
                <div ref={rowRefCallback} className="flex w-full min-w-full border-l bg-border h-[2.5rem]">
                    {hasCheckbox && <OptionCell />}
                    {hasRadio && <OptionCell />}
                    {hasIndex && <OptionCell />}
                    <div className="grid w-full" style={{ gridTemplateColumns }}>
                        <div className="bg-card flex items-center justify-center" style={{ gridColumn: "1 / -1" }}>
                            {t("msg.osc.00050")}
                        </div>
                    </div>
                </div>
            )}

            {/* Group */}
            {rowType === "group" && (
                <div ref={rowRefCallback} className="flex w-full min-w-full border-l bg-border h-[2.5rem]">
                    {!!Object.keys(_grid.current._group).length && (
                        <OptionCell bg="bg-card">
                            <IconButton
                                icon="right"
                                size="xs"
                                className={classNames(row.open && "rotate-45")}
                                onClick={() => _grid.current._handleGroup(row.groupKey, !row.open)}
                            />
                        </OptionCell>
                    )}
                    {hasCheckbox && <OptionCell bg="bg-card" />}
                    {hasRadio && <OptionCell bg="bg-card" />}
                    {hasIndex && <OptionCell bg="bg-card" />}
                    <div className="grid w-full" style={{ gridTemplateColumns }}>
                        {groupSchema?.map((schemaRow: any, rowIndex: any) => {
                            return schemaRow.map((cel: any, colIndex: any) => {
                                if (!cel) return null;
                                if (cel.show === false) return null;
                                const { binding, aggregate, align, text, decimalScale, thousandSeparator } = cel;
                                const celKey = rowKey + ".gg." + rowIndex + "." + colIndex;

                                const ag = row.aggregate.find((_: any) => {
                                    return _.binding === binding && _.aggregate === aggregate;
                                });
                                let value = ag?.value;

                                if (typeof value === "number") {
                                    if (typeof decimalScale === "number") {
                                        value = value.toFixed(decimalScale);
                                    }
                                    if (thousandSeparator) {
                                        const x = String(value).split(".");
                                        const int = Number(x[0]).toLocaleString();
                                        const dec = x[1];
                                        value = int + (dec === undefined ? "" : "." + dec);
                                    }
                                }

                                return (
                                    <pre
                                        key={celKey}
                                        className={classNames(
                                            "p-1 bg-card min-h-[2.5rem] border-r border-b flex items-center font-bold text-sm",
                                            (align === "start" || align === "left") && "justify-start text-left",
                                            (align === "end" || align === "right") && "justify-end text-right",
                                            (align === "center" || align === undefined) && "justify-center text-center",
                                        )}
                                        style={{
                                            gridRow: `${rowIndex + 1} / span ${cel.rowspan ?? 1}`,
                                            gridColumn: `${colIndex + 1} / span ${cel.colspan ?? 1}`,
                                        }}
                                    >
                                        {text ? t(text) : value}
                                    </pre>
                                );
                            });
                        })}
                    </div>
                </div>
            )}

            {/* GroupFoot */}
            {rowType === "groupFoot" && (
                <div ref={rowRefCallback} className="flex w-full min-w-full border-l bg-border h-[2.5rem]">
                    {hasGroup && <OptionCell bg="bg-card" />}
                    {hasCheckbox && <OptionCell bg="bg-card" />}
                    {hasRadio && <OptionCell bg="bg-card" />}
                    {hasIndex && <OptionCell bg="bg-card" />}
                    <div className="grid w-full" style={{ gridTemplateColumns }}>
                        {groupFootSchema?.map((schemaRow: any, rowIndex: any) => {
                            return schemaRow.map((cel: any, colIndex: any) => {
                                if (!cel) return null;
                                if (cel.show === false) return null;
                                const { binding, aggregate, text, align, decimalScale, thousandSeparator } = cel;
                                const celKey = rowKey + ".gg." + rowIndex + "." + colIndex;

                                const ag = row.aggregate?.find((_: any) => {
                                    return _.binding === binding && _.aggregate === aggregate;
                                });
                                let value = ag?.value;

                                if (typeof value === "number") {
                                    if (typeof decimalScale === "number") {
                                        value = value.toFixed(decimalScale);
                                    }
                                    if (thousandSeparator) {
                                        const x = String(value).split(".");
                                        const int = Number(x[0]).toLocaleString();
                                        const dec = x[1];
                                        value = int + (dec === undefined ? "" : "." + dec);
                                    }
                                }

                                const render = ag?.render;
                                if (render) {
                                    value = render(ag.datas);
                                }

                                // atchKeyValue 플래그: 라벨에 그룹 키 값을 덧붙임 (예: "Currency Sub Totals : TZS") !== 라벨에 그룹값 표시 ==!
                                // 콜론은 항상 붙이되, 키가 없으면(null/"null" 등) 값만 비움 !== 빈 키는 값만 생략, 콜론 유지 ==!
                                const _keyVal = row.value;
                                const _hasKey =
                                    _keyVal !== undefined &&
                                    _keyVal !== null &&
                                    _keyVal !== "" &&
                                    _keyVal !== "null" &&
                                    _keyVal !== "undefined";
                                const label = cel.atchKeyValue ? `${t(text)} : ${_hasKey ? _keyVal : ""}` : t(text);

                                return (
                                    <div
                                        key={celKey}
                                        className={classNames(
                                            "p-1 bg-card min-h-[2.5rem] flex border-r border-b items-center font-bold text-sm",
                                            (align === "start" || align === "left") && "justify-start text-left",
                                            (align === "end" || align === "right") && "justify-end text-right",
                                            (align === "center" || align === undefined) && "justify-center text-center",
                                        )}
                                        style={{
                                            gridRow: `${rowIndex + 1} / span ${cel.rowspan ?? 1}`,
                                            gridColumn: `${colIndex + 1} / span ${cel.colspan ?? 1}`,
                                        }}
                                    >
                                        {text ? label : value}
                                    </div>
                                );
                            });
                        })}
                    </div>
                </div>
            )}

            {/* Foot (grid-wide) — 그룹 무관 전체 합계 */}
            {rowType === "foot" && (
                <div ref={rowRefCallback} className="flex w-full min-w-full border-l bg-border h-[2.5rem]">
                    {hasGroup && <OptionCell bg="bg-card" />}
                    {hasCheckbox && <OptionCell bg="bg-card" />}
                    {hasRadio && <OptionCell bg="bg-card" />}
                    {hasIndex && <OptionCell bg="bg-card" />}
                    <div className="grid w-full" style={{ gridTemplateColumns }}>
                        {footSchema?.map((schemaRow: any, rowIndex: any) => {
                            return schemaRow.map((cel: any, colIndex: any) => {
                                if (!cel) return null;
                                if (cel.show === false) return null;
                                const { binding, aggregate, text, align, decimalScale, thousandSeparator } = cel;
                                const celKey = rowKey + ".gf." + rowIndex + "." + colIndex;

                                const ag = row.aggregate?.find((_: any) => {
                                    return _.binding === binding && _.aggregate === aggregate;
                                });
                                let value = ag?.value;

                                if (typeof value === "number") {
                                    if (typeof decimalScale === "number") {
                                        value = value.toFixed(decimalScale);
                                    }
                                    if (thousandSeparator) {
                                        const x = String(value).split(".");
                                        const int = Number(x[0]).toLocaleString();
                                        const dec = x[1];
                                        value = int + (dec === undefined ? "" : "." + dec);
                                    }
                                }

                                const render = ag?.render;
                                if (render) {
                                    value = render(ag.datas);
                                }

                                return (
                                    <div
                                        key={celKey}
                                        className={classNames(
                                            "p-1 bg-card min-h-[2.5rem] flex border-r border-b items-center font-bold text-sm",
                                            (align === "start" || align === "left") && "justify-start text-left",
                                            (align === "end" || align === "right") && "justify-end text-right",
                                            (align === "center" || align === undefined) && "justify-center text-center",
                                        )}
                                        style={{
                                            gridRow: `${rowIndex + 1} / span ${cel.rowspan ?? 1}`,
                                            gridColumn: `${colIndex + 1} / span ${cel.colspan ?? 1}`,
                                        }}
                                    >
                                        {text ? t(text) : value}
                                    </div>
                                );
                            });
                        })}
                    </div>
                </div>
            )}

            {/* Row */}
            {rowType !== "group" && rowType !== "groupFoot" && rowType !== "empty" && rowType !== "foot" && (
                <div
                    ref={rowRefCallback}
                    onClick={() => {
                        if (_grid.current._onRowClick) _grid.current._onRowClick(row);
                    }}
                    className={classNames(
                        "flex w-full min-w-full border-l bg-border",
                        rowType === "added"
                            ? "border-l-success"
                            : rowType === "updated"
                              ? "border-l-warning"
                              : "border-l-border",
                    )}
                >
                    {hasGroup && <OptionCell bg="bg-card" />}
                    {hasCheckbox && (
                        <OptionCell>
                            <input
                                type="checkbox"
                                disabled={
                                    checkbox === "readOnly" ||
                                    (_grid.current._render?.checkbox
                                        ? !_grid.current._render?.checkbox?.(row) || undefined
                                        : undefined)
                                }
                                checked={isChecked}
                                onChange={(event) => _grid.current._handleCheck(event, rowKey)}
                            />
                        </OptionCell>
                    )}
                    {hasRadio && (
                        <OptionCell>
                            <input
                                type="radio"
                                disabled={
                                    _grid.current._render?.radio
                                        ? !_grid.current._render?.radio?.(row) || undefined
                                        : undefined
                                }
                                checked={isSelected}
                                onChange={(event) => _grid.current._handleSelect(event, rowKey)}
                            />
                        </OptionCell>
                    )}
                    {hasIndex && (
                        <OptionCell>
                            {pagination === "out"
                                ? indexType === "DESC"
                                    ? (totalCount || 0) - rowIndex - page * size
                                    : rowIndex + 1 + page * size
                                : indexType === "DESC"
                                  ? totalCount - rowIndex
                                  : rowIndex + 1}
                        </OptionCell>
                    )}

                    {/* Body */}
                    {Body}
                </div>
            )}
        </div>
    );
}, areEqual);

const OptionCell = ({ children, bg = "bg-header" }: any) => {
    return (
        <div
            className={
                "flex items-center justify-center min-w-[2rem] max-w-[2rem] font-semibold border-r border-b" + ` ${bg}`
            }
        >
            {children}
        </div>
    );
};
